import { useEffect, useRef } from 'react';
import { PlayerEventType } from "../types";
import { normalizeFps } from '../utils/timecodeFormatter';
export function useMediaEvents(mediaEl, dispatch, callbacks, subscribersRef, 
/**
 * Frames per second of the media. Used by the pause handler to snap
 * currentTime to mid-frame of the actually-displayed frame so the
 * timecode display agrees with the visible image after pausing. Omit
 * for audio or when fps isn't known — snapping is then skipped.
 */
fps) {
    // Track whether media is currently playing so we can skip the
    // browser's low-frequency timeupdate dispatch during playback (the
    // RAF-based useFrameAccurateTime hook handles it at frame precision).
    const isPlayingRef = useRef(false);
    const waitingTimerRef = useRef(null);
    useEffect(() => {
        if (!mediaEl)
            return;
        const onTimeUpdate = () => {
            // During playback the RAF loop already dispatches TIME_UPDATE at
            // frame-level precision.  Skipping the browser timeupdate here
            // avoids redundant state updates and re-renders.
            if (!isPlayingRef.current) {
                dispatch({ type: 'TIME_UPDATE', currentTime: mediaEl.currentTime });
            }
            callbacks.onTimeUpdate?.(mediaEl.currentTime);
            // Send playback event to subcribers
            for (const subscriber of subscribersRef.current) {
                subscriber({ type: PlayerEventType.TIME_UPDATE, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
            }
        };
        const onPlay = () => {
            isPlayingRef.current = true;
            dispatch({ type: 'PLAY' });
            callbacks.onPlay?.();
            // Send playback event to subcribers
            for (const subscriber of subscribersRef.current) {
                subscriber({ type: PlayerEventType.PLAY, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
            }
        };
        // Pending pause-snap, deferred via rAF. We do NOT snap synchronously
        // inside the pause handler because incrementFrames / seekTo call
        // mediaEl.pause() then immediately set currentTime — the pause
        // event fires asynchronously AFTER the seek, and a synchronous
        // snap there would overwrite the step target with the previously
        // displayed frame's mid-point, producing a double-seek the user
        // feels as input lag. Instead we schedule the snap to run on the
        // next rAF and let the 'seeking' event (registered below) cancel
        // it if any seek — ours or the user's — is in flight by then.
        let pendingSnapHandle = null;
        const cancelPendingSnap = () => {
            if (pendingSnapHandle != null) {
                cancelAnimationFrame(pendingSnapHandle);
                pendingSnapHandle = null;
            }
        };
        const onPause = () => {
            isPlayingRef.current = false;
            // Schedule the snap. If a seek is already in flight (or starts
            // before the rAF callback runs), 'seeking' will cancel this.
            if (fps && fps > 0 && !mediaEl.seeking) {
                cancelPendingSnap();
                pendingSnapHandle = requestAnimationFrame(() => {
                    pendingSnapHandle = null;
                    // Re-check paused state — by now the user might have
                    // resumed playback (e.g. tapping space twice quickly).
                    if (!mediaEl.paused)
                        return;
                    // Snap to the visible frame's mid-point.
                    //
                    // Computing the visible frame from currentTime alone is a
                    // heuristic — the authoritative answer comes from
                    // requestVideoFrameCallback, but subscribing to it puts
                    // Chromium's video element on a slower decode path, which
                    // we tried and found made every seek visibly sluggish.
                    //
                    // The heuristic: when paused, currentTime usually lines up
                    // with the displayed frame's PTS, so floor(currentTime *
                    // fps) is the visible frame. But pause() can also stop the
                    // playback clock just past a frame boundary while the
                    // decoder hasn't yet pushed the next frame — currentTime
                    // says we're in frame N but the user still sees N-1. We
                    // can spot that case: if currentTime is very close to the
                    // LOWER edge of its frame interval, we're freshly across a
                    // boundary and the decoder almost certainly lags. The
                    // 0.1-of-a-frame threshold catches the "barely past
                    // boundary" case while leaving comfortably mid-frame
                    // pauses alone.
                    const trueFps = normalizeFps(fps);
                    const framePos = mediaEl.currentTime * trueFps;
                    const floored = Math.floor(framePos);
                    const fraction = framePos - floored;
                    const frame = fraction < 0.1 && floored > 0 ? floored - 1 : floored;
                    const target = (frame + 0.5) / trueFps;
                    if (Math.abs(mediaEl.currentTime - target) > 1e-6) {
                        mediaEl.currentTime = target;
                    }
                    dispatch({ type: 'TIME_UPDATE', currentTime: target });
                });
            }
            dispatch({ type: 'PAUSE' });
            callbacks.onPause?.();
            // Send playback event to subcribers
            for (const subscriber of subscribersRef.current) {
                subscriber({ type: PlayerEventType.PAUSE, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
            }
        };
        // Any seek in flight preempts the pending pause snap — that
        // includes seeks triggered by incrementFrames after it pauses, by
        // the user clicking the seek bar, by programmatic seekTo, etc.
        const onSeeking = () => {
            cancelPendingSnap();
        };
        // Some files (variable-bitrate, certain MP4 muxes, HLS) emit a
        // stream of `durationchange` events as the browser refines its
        // duration estimate while parsing more of the file. Each update
        // rescales the seek bar (position = currentTime / duration), which
        // looks like the playhead jumping around mid-playback. Filter the
        // noise without lagging reality:
        //
        //   - Reject Infinity / NaN / non-positive values outright.
        //   - Always accept the first valid value.
        //   - Always accept if the playhead would otherwise exceed the
        //     known duration (we MUST grow to contain currentTime,
        //     otherwise the seek bar fill overflows the bar's container).
        //   - Otherwise, only accept changes >= 0.5 s. That's well below
        //     human-noticeable but well above the typical parser jitter.
        let lastAcceptedDuration = 0;
        const setDuration = (d) => {
            if (!Number.isFinite(d) || d <= 0)
                return;
            // First valid value: accept unconditionally.
            if (lastAcceptedDuration === 0) {
                lastAcceptedDuration = d;
                dispatch({ type: 'DURATION_SET', duration: d });
                return;
            }
            // Playhead is at or near (within 1 s of) the known duration ceiling:
            // we must grow to contain it, otherwise currentTime / duration > 1
            // and the seek bar fill / markers overflow the bar's container.
            // Accept any growth in this regime.
            const playhead = mediaEl.currentTime;
            if (d > lastAcceptedDuration && playhead >= lastAcceptedDuration - 1.0) {
                lastAcceptedDuration = d;
                dispatch({ type: 'DURATION_SET', duration: d });
                return;
            }
            // Otherwise we're far from the edge — filter sub-0.5s noise so the
            // seek bar doesn't visibly rescale on every parser refinement tick.
            if (Math.abs(d - lastAcceptedDuration) < 0.5)
                return;
            lastAcceptedDuration = d;
            dispatch({ type: 'DURATION_SET', duration: d });
        };
        const onLoadedMetadata = () => {
            setDuration(mediaEl.duration);
            dispatch({ type: 'MEDIA_READY' });
        };
        const onDurationChange = () => {
            setDuration(mediaEl.duration);
        };
        const onProgress = () => {
            if (mediaEl.buffered.length > 0) {
                dispatch({
                    type: 'BUFFER_UPDATE',
                    bufferedEnd: mediaEl.buffered.end(mediaEl.buffered.length - 1),
                });
            }
        };
        const onWaiting = () => {
            // Debounce: only show the spinner if the waiting state persists
            // for more than 200ms. Short seeks (frame steps, small scrubs)
            // complete in <100ms and shouldn't flash the spinner.
            if (waitingTimerRef.current !== null)
                return;
            waitingTimerRef.current = setTimeout(() => {
                waitingTimerRef.current = null;
                dispatch({ type: 'SET_BUFFERING', isBuffering: true });
            }, 200);
        };
        const onCanPlay = () => {
            // Cancel the debounce timer if waiting resolves quickly.
            if (waitingTimerRef.current !== null) {
                clearTimeout(waitingTimerRef.current);
                waitingTimerRef.current = null;
            }
            dispatch({ type: 'SET_BUFFERING', isBuffering: false });
        };
        const onEnded = () => {
            isPlayingRef.current = false;
            // Snap the playhead to the very end so the UI shows the full duration.
            dispatch({ type: 'TIME_UPDATE', currentTime: mediaEl.duration });
            dispatch({ type: 'MEDIA_ENDED' });
            // The browser's native loop prevents the 'ended' event, so this
            // only fires when looping is off and playback genuinely ended.
            callbacks.onEnded?.();
            // Send playback event to subcribers
            for (const subscriber of subscribersRef.current) {
                subscriber({ type: PlayerEventType.PAUSE, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
            }
        };
        const onVolumeChange = () => {
            dispatch({ type: 'SET_VOLUME', volume: mediaEl.volume });
        };
        const onRateChange = () => {
            dispatch({ type: 'SET_PLAYBACK_RATE', rate: mediaEl.playbackRate });
            // Emit SPEED_CHANGE (not SEEK — a rate change is not a seek).
            // The prior code emitted SEEK here which caused sync followers to
            // treat every rate change as a seek target, corrupting position
            // sync during playback rate changes.
            for (const subscriber of subscribersRef.current) {
                subscriber({ type: PlayerEventType.SPEED_CHANGE, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
            }
        };
        mediaEl.addEventListener('timeupdate', onTimeUpdate);
        mediaEl.addEventListener('play', onPlay);
        mediaEl.addEventListener('pause', onPause);
        mediaEl.addEventListener('seeking', onSeeking);
        mediaEl.addEventListener('loadedmetadata', onLoadedMetadata);
        mediaEl.addEventListener('durationchange', onDurationChange);
        mediaEl.addEventListener('progress', onProgress);
        mediaEl.addEventListener('waiting', onWaiting);
        mediaEl.addEventListener('canplay', onCanPlay);
        mediaEl.addEventListener('ended', onEnded);
        mediaEl.addEventListener('volumechange', onVolumeChange);
        mediaEl.addEventListener('ratechange', onRateChange);
        // If metadata already loaded
        if (mediaEl.readyState >= 1) {
            setDuration(mediaEl.duration);
            dispatch({ type: 'MEDIA_READY' });
        }
        return () => {
            if (waitingTimerRef.current !== null) {
                clearTimeout(waitingTimerRef.current);
                waitingTimerRef.current = null;
            }
            cancelPendingSnap();
            mediaEl.removeEventListener('timeupdate', onTimeUpdate);
            mediaEl.removeEventListener('play', onPlay);
            mediaEl.removeEventListener('pause', onPause);
            mediaEl.removeEventListener('seeking', onSeeking);
            mediaEl.removeEventListener('loadedmetadata', onLoadedMetadata);
            mediaEl.removeEventListener('durationchange', onDurationChange);
            mediaEl.removeEventListener('progress', onProgress);
            mediaEl.removeEventListener('waiting', onWaiting);
            mediaEl.removeEventListener('canplay', onCanPlay);
            mediaEl.removeEventListener('ended', onEnded);
            mediaEl.removeEventListener('volumechange', onVolumeChange);
            mediaEl.removeEventListener('ratechange', onRateChange);
        };
    }, [mediaEl, dispatch, callbacks, subscribersRef, fps]);
}
